import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  PersonAdd,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  ContentCut as ServicesIcon,
  FileUploadOutlined,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { PaginatedTable, CreateAccountForm } from "../../components";
import ImportStylistsDialog from "./ImportStylistsDialog";
import { baseAxios } from "../../utils/config";
import { colors } from "..";

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        color={active ? "primary" : "default"}
        sx={{
          p: 0.35,
          color: active ? "primary.main" : "rgba(255, 255, 255, 0.85)",
          opacity: active ? 1 : 0.85,
          "&:hover": {
            color: "common.white",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, mt: 0.5 },
          },
        }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
            sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function stylistDisplayName(row) {
  const first = String(row.user_first_name ?? "").trim();
  const last = String(row.user_last_name ?? "").trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  return "";
}

function stylistMatchesSearch(row, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;

  const blob = [
    row.location_name,
    row.user_first_name,
    row.user_last_name,
    row.user_email,
    stylistDisplayName(row),
  ]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");

  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((tok) => blob.includes(tok));
}

function compareStylistRows(a, b, field) {
  let primary = 0;

  if (field === "display_name") {
    const sa = stylistDisplayName(a);
    const sb = stylistDisplayName(b);
    primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  } else {
    const va = a[field];
    const vb = b[field];
    const sa = va == null ? "" : String(va);
    const sb = vb == null ? "" : String(vb);
    primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  }

  if (primary !== 0) return primary;
  return (Number(a.user_id) || 0) - (Number(b.user_id) || 0);
}

export default function SearchStylists() {
  const navigate = useNavigate();

  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [stylists, setStylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterLocation, setFilterLocation] = useState("");
  const [locationFilterMenuAnchor, setLocationFilterMenuAnchor] =
    useState(null);
  const [sortBy, setSortBy] = useState("display_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const openCreateStylist = useCallback(() => {
    setAccountDialogOpen(true);
  }, []);

  const closeAccountDialog = useCallback(() => {
    setAccountDialogOpen(false);
  }, []);

  const openStylistServices = useCallback(
    (userId) => {
      navigate({
        pathname: "/search-stylists/stylist-services",
        search: `?param=${userId}`,
      });
    },
    [navigate]
  );

  const loadStylists = useCallback(() => {
    setLoading(true);
    baseAxios
      .get("/stylists")
      .then((res) => {
        const data = res?.data;
        setStylists(Array.isArray(data) ? data : []);
        if (!Array.isArray(data)) {
          console.error("Stylists data is not an array:", data);
        }
      })
      .catch(() => {
        setStylists([]);
        toast.error("Could not load stylists.", { duration: 3000 });
      })
      .finally(() => setLoading(false));
  }, []);

  const onStylistSaved = useCallback(() => {
    loadStylists();
    closeAccountDialog();
  }, [loadStylists, closeAccountDialog]);

  useEffect(() => {
    loadStylists();
  }, [loadStylists]);

  const locationFilterOptions = useMemo(() => {
    const set = new Set();
    for (const row of stylists) {
      const name = String(row.location_name ?? "").trim();
      if (name) set.add(name);
    }
    return [...set].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [stylists]);

  const filteredStylists = useMemo(() => {
    return stylists.filter((row) => {
      if (!stylistMatchesSearch(row, deferredSearch)) return false;
      if (filterLocation && row.location_name !== filterLocation) return false;
      return true;
    });
  }, [stylists, deferredSearch, filterLocation]);

  const sortedStylists = useMemo(() => {
    const copy = [...filteredStylists];
    copy.sort((a, b) => {
      const c = compareStylistRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredStylists, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const columns = useMemo(() => {
    const sq = deferredSearch;
    return [
      {
        id: "location_name",
        label: "Location",
        field: "location_name",
        minWidth: 180,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by location"
            value={filterLocation}
            onChange={setFilterLocation}
            anchorEl={locationFilterMenuAnchor}
            setAnchorEl={setLocationFilterMenuAnchor}
            allMenuLabel="All locations"
            options={locationFilterOptions.map((label) => ({
              value: label,
              label,
            }))}
          />
        ),
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "display_name",
        label: "Name",
        field: "display_name",
        render: (_, row) => {
          const name = stylistDisplayName(row);
          return name ? <HighlightText text={name} query={sq} /> : "-";
        },
      },
      {
        id: "user_email",
        label: "Email",
        field: "user_email",
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        minWidth: 140,
        render: (_, row) => (
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<ServicesIcon sx={{ fontSize: 14 }} />}
            onClick={() => openStylistServices(row.user_id)}
            sx={{
              minHeight: 24,
              py: 0.25,
              px: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : colors.black,
              "& .MuiButton-startIcon": { mr: 0.5, color: "primary.main" },
            }}
          >
            Services
          </MuiButton>
        ),
      },
    ];
  }, [
    deferredSearch,
    filterLocation,
    locationFilterMenuAnchor,
    locationFilterOptions,
    openStylistServices,
  ]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Stylists
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View stylists and manage the services assigned to each one.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search location, name, or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: "grey.400",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh stylists"
            onClick={loadStylists}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton
            variant="outlined"
            color="primary"
            onClick={() => setImportDialogOpen(true)}
            startIcon={<FileUploadOutlined />}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "background.paper",
            }}
          >
            Import Stylists
          </MuiButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={openCreateStylist}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <PersonAdd sx={{ mr: 0.75 }} /> Add Stylist
          </MuiButton>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedStylists}
        totalRowCount={sortedStylists.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="display_name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterLocation}`}
        clientSidePagination
        skeletonHeight="40px"
      />

      <Dialog
        open={accountDialogOpen}
        onClose={closeAccountDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "secondary.main",
            color: "common.white",
            borderBottom: "1px solid",
            borderColor: "primary.main",
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="inherit">
              Add stylist
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
            >
              Create a new stylist account with location and availability.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={closeAccountDialog}
            size="small"
            sx={{
              color: "primary.main",
              mt: 0.25,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <CreateAccountForm
            key="new-stylist"
            stylistPreset
            onSaved={onStylistSaved}
            onRequestClose={closeAccountDialog}
          />
        </DialogContent>
      </Dialog>

      <ImportStylistsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={loadStylists}
      />
    </Box>
  );
}
